import { useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Shared presentational pieces for the v7 Settings sections (SCRUM-460),
 * token-faithful ports of the prototype `.settings-card` / `.settings-row` /
 * `.settings-list-item` families (app-shell 7.html). No scoped stylesheet —
 * every value is a design-token utility.
 */

/** A titled card container (`.settings-card` + `__hdr` + `__title` + `__body`). */
export function SettingsCard({
  title,
  headerRight,
  children,
}: {
  title: string;
  headerRight?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="bg-bg-4 border-border-0 rounded-tk-lg overflow-hidden border">
      <div className="border-border-0 px-tk-lg flex items-center justify-between gap-2.5 border-b py-3.5">
        <div className="text-text-0 font-display text-[0.9375rem] font-semibold">{title}</div>
        {headerRight}
      </div>
      <div className="px-tk-lg py-tk-md flex flex-col">{children}</div>
    </div>
  );
}

/** A section wrapper stacking cards (`.settings-section`). */
export function SettingsSection({ children }: { children: ReactNode }) {
  return <div className="gap-tk-lg flex flex-col">{children}</div>;
}

/** A label/value row (`.settings-row`). */
export function SettingsRow({
  label,
  sub,
  children,
  className,
}: {
  label: string;
  sub?: string | undefined;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <div
      className={cn(
        'border-border-0 gap-tk-md flex items-center justify-between border-b py-3 last:border-b-0',
        className,
      )}
    >
      <div className="text-text-1 flex min-w-0 flex-col gap-0.5 text-[0.8125rem]">
        {label}
        {sub ? <span className="text-text-3 text-[0.6875rem] font-normal">{sub}</span> : null}
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

const pencilIcon = (
  <svg
    viewBox="0 0 12 12"
    width="11"
    height="11"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8.5 1.5l2 2M3 7l-1 3 3-1L11 2.5l-2-2L3 7z" />
  </svg>
);

/**
 * An inline-editable value row (`renderSettingsEditableRow`): a display value +
 * pencil button that swaps to an input with Save/Cancel. Controlled by the
 * caller via `value`/`onSave`.
 */
export function EditableRow({
  label,
  help,
  value,
  onSave,
  type = 'text',
  placeholder,
}: {
  label: string;
  help?: string | undefined;
  value: string;
  onSave: (next: string) => void | Promise<void>;
  type?: string | undefined;
  placeholder?: string | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const start = () => {
    setDraft(value);
    setEditing(true);
  };
  const commit = async () => {
    await onSave(draft);
    setEditing(false);
  };

  return (
    <SettingsRow label={label} sub={help}>
      {editing ? (
        <div className="flex max-w-[380px] flex-1 gap-1.5">
          <input
            aria-label={label}
            type={type}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            className="bg-bg-3 border-brand-primary rounded-tk-sm text-text-0 font-body flex-1 border px-2.5 py-1.5 text-[0.8125rem] outline-none"
          />
          <button
            type="button"
            onClick={() => void commit()}
            className="rounded-tk-md shadow-tk-sm inline-flex items-center px-3 py-1.5 text-[0.75rem] font-semibold text-white [background:var(--grad-cyan)]"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
            }}
            className="text-text-3 hover:text-text-0 font-body border-0 bg-transparent px-2.5 py-1.5 text-[0.75rem]"
          >
            Cancel
          </button>
        </div>
      ) : (
        <>
          <span className="text-text-0 text-[0.8125rem] font-medium">
            {value || <span className="text-text-3">—</span>}
          </span>
          <button
            type="button"
            aria-label={`Edit ${label}`}
            onClick={start}
            className="text-text-3 hover:bg-bg-3 hover:border-border-1 hover:text-text-0 rounded-tk-sm inline-flex h-[26px] w-[26px] items-center justify-center border border-transparent bg-transparent"
          >
            {pencilIcon}
          </button>
        </>
      )}
    </SettingsRow>
  );
}

/** A device/session list item (`.settings-list-item`). */
export function SettingsListItem({
  name,
  sub,
  action,
}: {
  name: string;
  sub: string;
  action?: ReactNode;
}) {
  return (
    <div className="border-border-0 gap-tk-md flex items-center justify-between border-b py-2.5 last:border-b-0">
      <div>
        <div className="text-text-0 text-[0.8125rem] font-medium">{name}</div>
        <div className="text-text-3 mt-0.5 text-[0.6875rem]">{sub}</div>
      </div>
      {action}
    </div>
  );
}

/** The secondary list button (`.settings-list-item__btn`), optional danger variant. */
export function ListItemButton({
  children,
  onClick,
  danger,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'bg-bg-3 border-border-1 rounded-tk-sm text-text-1 font-body border px-3 py-1.5 text-[0.6875rem] font-medium',
        danger
          ? 'hover:border-[rgba(200,74,74,0.40)] hover:bg-[rgba(200,74,74,0.10)] hover:text-[#c84a4a]'
          : 'hover:bg-bg-2 hover:text-text-0',
        className,
      )}
    >
      {children}
    </button>
  );
}
